import type React from 'react';
import type { TFunction } from 'i18next';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { OptionMarkedLabel } from '@/js/libs/ui/src/V2';

import type { Option } from 'src/types';

export type GetMarkedOptionLabel<T = unknown> = (option: T) => React.ReactNode | string;

interface MarkedOptionLabelBaseProps<T, V = unknown> {
  t: TFunction;
  markedMarker?: string;
  inactiveMarker?: string;
  toOption?: (selectedItem: T) => Option<V>;
  formatLabel?: (option: Pick<Option<V>, 'label' | 'value'>) => string;
}

interface MarkedOptionLabelPositiveProps<T, V = unknown> extends MarkedOptionLabelBaseProps<T, V> {
  positive?: boolean;
  warning?: never;
}

interface MarkedOptionLabelWarningProps<T, V = unknown> extends MarkedOptionLabelBaseProps<T, V> {
  positive?: never;
  warning?: boolean;
}

type MarkedOptionLabelProps<T, V = unknown> = MarkedOptionLabelPositiveProps<T, V> | MarkedOptionLabelWarningProps<T, V>;

const isOption = (option: unknown): option is Option<unknown> =>
  typeof option === 'object' && option !== null && 'label' in option && 'value' in option;

/**
 * Custom hook to generate a marked option label based on the option's properties.
 *
 * @param {TFunction} t - The translation function from i18next.
 * @param markedMarker - The marker to use for marked options instead of default.
 * @param inactiveMarker - The marker to use for inactive options instead of default.
 * @param positive - Whether to mark the option as positive.
 * @param warning - Whether to mark the option as a warning.
 * @param toOption - A function to convert the selected item to Option type.
 * @param formatLabel - A function to transform the option label before rendering (e.g. appending an ID).
 * @returns {GetMarkedOptionLabel} - A function that returns a React node or string based on the option's properties.
 */
export function useMarkedOptionLabel<T, V = unknown>({
  t,
  positive,
  warning,
  markedMarker,
  inactiveMarker,
  toOption,
  formatLabel,
}: MarkedOptionLabelProps<T, V>): GetMarkedOptionLabel<T> {
  const markedMarkerValue = markedMarker ?? t('common:DEFAULT');

  return useCallbackRef((selectedItem) => {
    let option: Option<V>;
    if (!isOption(selectedItem)) {
      if (!toOption) throw new Error('Pass values must be Option. Pass "toOption" to convert to Option.');
      option = toOption(selectedItem);
    } else {
      option = selectedItem as Option<V>;
    }

    const displayOption = formatLabel ? { ...option, label: formatLabel(option) } : option;

    if (displayOption.marked) {
      if (positive) {
        return <OptionMarkedLabel option={displayOption} positive marker={markedMarkerValue} />;
      }
      if (warning) {
        return <OptionMarkedLabel option={displayOption} warning marker={markedMarkerValue} />;
      }
      return <OptionMarkedLabel option={displayOption} informative marker={markedMarkerValue} />;
    }
    if (typeof displayOption.active === 'boolean' && !displayOption.active) {
      return <OptionMarkedLabel option={displayOption} negative marker={inactiveMarker ?? t('common:INACTIVE')} />;
    }
    return displayOption.label;
  });
}
